import React, { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, MapPin, Clock, Users, DollarSign, Lock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { eventsService, ticketsService } from '../services/api-service';
import './Calendar.css';

const Calendar = () => {
  const { user, isSubscribed } = useAuth();
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const events = await eventsService.getUpcomingEvents();
      setUpcomingEvents(events);
    } catch (error) {
      console.error('Error loading events:', error);
    } finally {
      setLoading(false);
    }
  };

  // Group events by month
  const eventsByMonth = upcomingEvents.reduce((acc, event) => {
    const date = new Date(event.date);
    const monthYear = date.toLocaleDateString('en-AU', { 
      month: 'long', 
      year: 'numeric' 
    });
    
    if (!acc[monthYear]) {
      acc[monthYear] = [];
    }
    acc[monthYear].push(event);
    return acc;
  }, {});

  const handlePurchaseTicket = (event) => {
    setSelectedEvent(event);
    setShowTicketModal(true);
  };

  const getEventPrice = (event) => {
    // Price logic - free for subscribers, $20 for others, or event-specific price
    if (isSubscribed) return 0;
    return event.price !== undefined ? event.price : 20;
  };

  return (
    <div className="calendar-page">
      {/* Header */}
      <section className="calendar-header">
        <div className="container">
          <div className="calendar-header-content">
            <CalendarIcon size={48} className="calendar-header-icon" />
            <h1>Event Calendar</h1>
            <p>
              {user ? (
                <>
                  {isSubscribed 
                    ? `Welcome back, ${user.firstName || user.name}! You have FREE access to all events as a subscriber.`
                    : `Welcome, ${user.firstName || user.name}! Subscribe to get free entry to all events.`
                  }
                </>
              ) : (
                'Login to see event details and purchase tickets'
              )}
            </p>
            {loading && <p style={{ marginTop: '10px', color: '#666' }}>Loading events...</p>}
          </div>
        </div>
      </section>

      {/* Subscription Banner (if not subscribed) */}
      {!isSubscribed && (
        <section className="calendar-subscribe-banner">
          <div className="container">
            <div className="subscribe-banner-content">
              <h3>🎉 Get FREE Entry to All Events!</h3>
              <p>
                Subscribe to B2P and enjoy free entry to all events, priority location reveals, 
                and exclusive after-party invitations.
              </p>
              <a href="/subscribe" className="btn btn-white">
                Subscribe Now
              </a>
            </div>
          </div>
        </section>
      )}

      {/* Calendar View */}
      <section className="calendar-section section">
        <div className="container">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px' }}>
              <p>Loading events...</p>
            </div>
          ) : Object.keys(eventsByMonth).length > 0 ? (
            Object.entries(eventsByMonth).map(([month, events]) => (
              <div key={month} className="calendar-month">
                <h2 className="calendar-month-title">{month}</h2>
                <div className="calendar-events-grid">
                  {events.map((event) => {
                    const artists = []; // Artists API not yet implemented
                    const price = getEventPrice(event);
                    const locationRevealed = isSubscribed || event.status === 'completed';

                    return (
                      <div key={event.id} className="calendar-event-card">
                        {/* Event Flyer */}
                        {(event.flyer || event.coverImage) && (
                          <div className="calendar-event-image">
                            <img src={event.flyer || event.coverImage} alt={event.title} />
                            {price === 0 && (
                              <span className="calendar-event-free-badge">FREE</span>
                            )}
                          </div>
                        )}

                        {/* Event Details */}
                        <div className="calendar-event-content">
                          <h3 className="calendar-event-title">{event.title}</h3>
                          
                          <div className="calendar-event-meta">
                            <div className="calendar-event-meta-item">
                              <CalendarIcon size={18} />
                              <span>
                                {new Date(event.date).toLocaleDateString('en-AU', {
                                  weekday: 'long',
                                  month: 'long',
                                  day: 'numeric',
                                  year: 'numeric'
                                })}
                              </span>
                            </div>

                            {/* Time not available from backend yet */}

                            <div className="calendar-event-meta-item">
                              <MapPin size={18} />
                              {locationRevealed ? (
                                <span>{typeof event.location === 'string' ? event.location : (event.location?.venue || event.location?.address || 'Location TBA')}</span>
                              ) : (
                                <span className="calendar-location-locked">
                                  <Lock size={14} />
                                  Revealed after purchase
                                </span>
                              )}
                            </div>

                            {event.attendees && (
                              <div className="calendar-event-meta-item">
                                <Users size={18} />
                                <span>{event.attendees} attendees</span>
                              </div>
                            )}
                          </div>

                          {/* Theme */}
                          {event.theme && (
                            <div className="calendar-event-theme">
                              <strong>Theme:</strong> {event.theme}
                            </div>
                          )}

                          {/* Artists */}
                          {artists.length > 0 && (
                            <div className="calendar-event-artists">
                              <strong>Artists:</strong>
                              <div className="calendar-event-artists-list">
                                {artists.map((artist) => (
                                  <span key={artist.id} className="calendar-artist-tag">
                                    {artist.name}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Price & Purchase Button */}
                          <div className="calendar-event-actions">
                            <div className="calendar-event-price">
                              {price === 0 ? (
                                <span className="calendar-price-free">
                                  FREE (Subscriber)
                                </span>
                              ) : (
                                <span className="calendar-price-amount">
                                  <DollarSign size={20} />
                                  ${price} AUD
                                </span>
                              )}
                            </div>
                            <button
                              className="btn calendar-buy-btn"
                              onClick={() => handlePurchaseTicket(event)}
                            >
                              {price === 0 ? 'RSVP FREE' : 'Purchase Ticket'}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          ) : (
            <div className="calendar-empty">
              <CalendarIcon size={64} className="calendar-empty-icon" />
              <h3>No Upcoming Events Yet</h3>
              <p>Check back soon for new celebrations!</p>
            </div>
          )}
        </div>
      </section>

      {/* Ticket Purchase Modal */}
      {showTicketModal && selectedEvent && (
        <TicketModal
          event={selectedEvent}
          price={getEventPrice(selectedEvent)}
          isSubscribed={isSubscribed}
          onClose={() => {
            setShowTicketModal(false);
            setSelectedEvent(null);
          }}
        />
      )}
    </div>
  );
};

// Ticket Purchase Modal Component
const TicketModal = ({ event, price, isSubscribed, onClose }) => {
  const [quantity, setQuantity] = useState(1);
  const [processing, setProcessing] = useState(false);

  const total = price * quantity;

  const handlePurchase = async () => {
    setProcessing(true);

    try {
      // Bypass external payment - test internal flow only
      // Create ticket in database
      const ticketData = {
        eventId: event.id,
        quantity: quantity,
        amount: total,
        // Payment bypassed for testing - mark as paid
        paymentStatus: 'paid',
        paymentMethod: 'test',
        // Note: In production, this would come from payment processor
      };

      const response = await ticketsService.createTicket(ticketData);
      
      if (response.success || response.data) {
        alert(`Success! You've ${price === 0 ? 'RSVP\'d for' : 'purchased tickets to'} ${event.title}!\n\nYour ticket${quantity > 1 ? 's are' : ' is'} available in your profile. Location details will be sent to you closer to the event.`);
        onClose();
        // Optionally reload events or redirect to profile
        window.location.href = '/profile';
      } else {
        throw new Error('Ticket creation failed');
      }
      
    } catch (error) {
      console.error('Error purchasing ticket:', error);
      alert(`Purchase failed: ${error.message || 'Please try again.'}`);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="ticket-modal-overlay" onClick={onClose}>
      <div className="ticket-modal" onClick={(e) => e.stopPropagation()}>
        <h3>{price === 0 ? 'RSVP for Event' : 'Purchase Tickets'}</h3>
        <h2>{event.title}</h2>
        
        <div className="ticket-modal-details">
          <p>
            <CalendarIcon size={16} />
            {new Date(event.date).toLocaleDateString('en-AU', {
              weekday: 'long',
              month: 'long',
              day: 'numeric'
            })}
          </p>
          {event.time && (
            <p>
              <Clock size={16} />
              {event.time.start} - {event.time.end}
            </p>
          )}
        </div>

        {price > 0 && (
          <div className="ticket-modal-quantity">
            <label>Quantity:</label>
            <div className="quantity-selector">
              <button onClick={() => setQuantity(Math.max(1, quantity - 1))}>-</button>
              <span>{quantity}</span>
              <button onClick={() => setQuantity(quantity + 1)}>+</button>
            </div>
          </div>
        )}

        <div className="ticket-modal-total">
          <span>Total:</span>
          <span className="ticket-modal-price">
            {total === 0 ? 'FREE' : `$${total} AUD`}
          </span>
        </div>

        {isSubscribed && (
          <div className="ticket-modal-subscriber-note">
            🎉 Free entry as a B2P subscriber!
          </div>
        )}

        <div className="ticket-modal-actions">
          <button onClick={onClose} className="btn btn-outline">
            Cancel
          </button>
          <button 
            onClick={handlePurchase} 
            className="btn"
            disabled={processing}
          >
            {processing ? 'Processing...' : (price === 0 ? 'Confirm RSVP' : 'Pay Now')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Calendar;

